import type {
  RoleAssignment,
  RoleWithPermissions,
} from '../../../domain/access/permission-resolver';
import type { RoleId, UserId } from '../../../domain/shared/ids';
import { ok, type Result } from '../../../domain/shared/result';
import type { AppError } from '../../errors/app-error';
import type { Session } from '../../session';

/**
 * Quem sou eu, e o que posso.
 *
 * A peça que faltava para a interface viver sobre o servidor. `SignInWithPassword` devolve
 * o segredo da sessão, o id e o nome — e nada mais: nem os papéis, nem a matriz de
 * permissões. Sem eles a tela não tem como decidir o que mostrar, e ficaria oferecendo
 * botões que o servidor recusaria depois, um a um.
 *
 * ## Por que não é o `LoadSession` exposto
 *
 * `LoadSession` recebe um **`userId`** e monta a sessão daquele perfil. Como endpoint,
 * seria a permissão de vestir a identidade de qualquer pessoa cadastrada — e é
 * exatamente por isso que o servidor não o põe no container: quem o chama é a entrada,
 * por dentro, ao resolver o token.
 *
 * Este não recebe nada. O sujeito é o do **token**, resolvido pelo servidor antes de a
 * chamada chegar aqui, e a resposta é sobre ele e mais ninguém. A diferença entre os dois
 * não é de grau: um responde "quem é fulano?", o outro responde "quem sou eu?", e a
 * segunda pergunta quem faz já sabe a resposta.
 *
 * **Não verifica permissão, e é o único caso em que isso é a resposta certa** — não existe
 * permissão para saber quem você é. Ter a sessão é a autorização inteira, e é o que a
 * lista `AUTHENTICATED_ONLY` registra, com motivo escrito.
 */

/**
 * A sessão como ela atravessa a rede.
 *
 * `Session.roles` é um `Map`, e `Map` não sobrevive ao JSON: `JSON.stringify` de um Map
 * produz `{}` — silenciosamente, sem erro, e a tela receberia um perfil sem permissão
 * nenhuma em vez de uma falha. Então a travessia é feita em lista, e quem recebe remonta.
 *
 * `projectId` e `authSessionId` não vão junto de propósito: a obra em contexto é escolha
 * da tela a cada chamada, e o id da sessão do aparelho é segredo do servidor.
 */
export interface MySessionView {
  readonly userId: UserId;
  readonly userName: string;
  readonly userEmail?: string;
  readonly assignments: readonly RoleAssignment[];
  readonly roles: readonly (readonly [RoleId, RoleWithPermissions])[];
}

export class GetMySession {
  execute(session: Session): Promise<Result<MySessionView, AppError>> {
    return Promise.resolve(
      ok({
        userId: session.userId,
        userName: session.userName,
        ...(session.userEmail === undefined ? {} : { userEmail: session.userEmail }),
        assignments: session.assignments,
        roles: [...session.roles],
      }),
    );
  }
}

/** O caminho de volta: a lista vira o `Map` que o `PermissionChecker` espera. */
export function sessionFromView(view: MySessionView): Session {
  return {
    userId: view.userId,
    userName: view.userName,
    ...(view.userEmail === undefined ? {} : { userEmail: view.userEmail }),
    assignments: view.assignments,
    roles: new Map(view.roles),
  };
}
